import { startSupportMeasureReportPdfExport } from '@/api/resources/supportMeasureEntry.api';
import { useAnalytics } from '@/composables/analytics/useAnalytics';
import { useFileUrl } from '@/composables/files/useFileUrl';
import { useConfirmDialog } from '@/composables/useConfirmDialog';
import { usePdfExportOperation } from '@/composables/usePdfExportOperation';
import { useToast } from '@/composables/useToast';
import { AnalyticsEvents } from '@/services/analytics/events';
import { EIcon } from 'common/enum/misc.enum';
import type { IPdfExportResult } from 'common/interface/pdf-export.interface';
import type { ISupportMeasureReportOptions } from 'common/interface/support-measure-report.interface';
import { computed, ref, type ComputedRef } from 'vue';
import { useI18n } from 'vue-i18n';

interface IDownloadSupportMeasureReportsParams {
  entryId: number;
  /** Omitted for all current members of the measure. */
  studentIds?: number[];
  studentCount: number;
  options: ISupportMeasureReportOptions;
}

interface ISupportMeasureReportPdfComposable {
  loading: ComputedRef<boolean>;
  downloadReports: (params: IDownloadSupportMeasureReportsParams) => Promise<void>;
}

export const useSupportMeasureReportPdf = (): ISupportMeasureReportPdfComposable => {
  const { runPdfExport } = usePdfExportOperation();
  const { showError } = useToast();
  const { download } = useFileUrl();
  const { confirm } = useConfirmDialog();
  const { trackEvent } = useAnalytics();
  const { t } = useI18n();

  const loadingState = ref(false);
  const loading = computed(() => loadingState.value);

  // Confirm before resolving the current authorized download URL and saving the PDF.
  const confirmDownload = async (result: IPdfExportResult, studentCount: number): Promise<void> => {
    const confirmed = await confirm({
      title: t('supportMeasureEntry.report.confirmTitle', studentCount),
      description: t('supportMeasureEntry.report.confirmDescription', studentCount),
      confirmButtonLabel: t('supportMeasureEntry.report.download'),
      declineButtonLabel: t('general.cancel'),
      theme: 'primary',
      size: 'lg',
      confirmButtonIcon: EIcon.DOWNLOAD,
    });
    if (!confirmed) return;

    await download(result.fileId, result.filename);
    trackEvent(AnalyticsEvents.supportMeasure.reportDownloaded);
  };

  const downloadReports = async (params: IDownloadSupportMeasureReportsParams): Promise<void> => {
    loadingState.value = true;
    let result: IPdfExportResult;
    try {
      result = await runPdfExport(() =>
        startSupportMeasureReportPdfExport({
          id: params.entryId,
          dto: { ...params.options, studentIds: params.studentIds },
        }),
      );
    } catch (error) {
      console.error('Support measure report PDF generation failed', error);
      showError(error, { UNKNOWN: 'supportMeasureEntry.report.generationFailed' });
      return;
    } finally {
      loadingState.value = false;
    }

    try {
      await confirmDownload(result, params.studentCount);
    } catch (error) {
      showError(error, { UNKNOWN: 'supportMeasureEntry.report.generationFailed' });
    }
  };

  return { loading, downloadReports };
};
